import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../api/client";
import {
  asCurl,
  jobStatusPath,
  parseBatch,
  storagePreviewPath,
  submitDocument,
  submitRawDocument,
  type BatchRow,
  type SubmitInput,
  type SubmitResult,
} from "../api/submit";
import type { DocumentDetail } from "../api/types";
import { DocumentViewer } from "../components/DocumentViewer";
import { ErrorState } from "../components/States";
import { Breadcrumb } from "../components/Toolbar";
import { fmtMs } from "../lib/format";

/** Nothing else is running once a job reaches one of these. */
const TERMINAL = ["completed", "failed"];

/**
 * Gap between submissions in a batch.
 *
 * Ingestion has its own nginx zone at 10 r/s with a burst of 10. Two documents
 * would never notice; fifty would, and a 429 halfway through a batch is a
 * confusing way to learn about a rate limit.
 */
const BATCH_GAP_MS = 150;

/** What one row of a running batch is doing. */
interface BatchState {
  row: BatchRow;
  status:
    | "waiting"
    | "submitting"
    | "queued"
    | "deduped"
    | "completed"
    | "failed"
    | "refused";
  jobId?: string;
  documentId?: string;
  fields?: number;
  note?: string;
}

/** Stop polling rather than watch a tab forever; the document page still works. */
const POLL_CEILING_MS = 5 * 60 * 1000;

/**
 * Fast for the fast path, slower for the slow one.
 *
 * The pipeline finishes some documents in seconds and takes minutes over
 * others, so a single interval either hammers the API or leaves a quick result
 * sitting unseen.
 */
function pollDelay(elapsedMs: number): number {
  return elapsedMs < 30_000 ? 2000 : 5000;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/**
 * Paste a JSON array, review it, then run it.
 *
 * Submitting is browser-driven and takes a moment per document, but the work
 * itself is not: `submit` answers 202 and the pipeline continues server-side.
 * Closing this tab therefore stops the live progress table, not the extraction
 * — which is worth saying on screen, because "background" usually implies the
 * opposite and an operator who closes the tab should know what they kept.
 */
function BatchPanel({ tenantId }: { tenantId: string }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState<BatchRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [states, setStates] = useState<BatchState[] | null>(null);
  const [running, setRunning] = useState(false);
  const [force, setForce] = useState(false);
  const cancelled = useRef(false);

  useEffect(
    () => () => {
      cancelled.current = true;
    },
    [],
  );

  const review = (raw: string) => {
    setStates(null);
    if (!raw.trim()) {
      setRows(null);
      setParseError(null);
      return;
    }
    const { rows: parsed, error } = parseBatch(raw);
    setParseError(error);
    setRows(error ? null : parsed);
  };

  const readFile = (f: File | null) => {
    if (!f) return;
    f.text().then((t) => {
      setText(t);
      review(t);
    });
  };

  const run = async () => {
    if (!rows) return;
    cancelled.current = false;
    setRunning(true);

    const initial: BatchState[] = rows.map((row) => ({
      row,
      status: "waiting",
    }));
    setStates(initial);
    const live = [...initial];
    const update = (i: number, patch: Partial<BatchState>) => {
      live[i] = { ...live[i], ...patch };
      setStates([...live]);
    };

    // Submit first, all of them, before following any. A document queued on the
    // server is already being worked on, so getting them all in beats watching
    // the first one finish.
    for (let i = 0; i < rows.length; i++) {
      if (cancelled.current) break;
      const { body, problems } = rows[i];
      if (problems.length > 0) {
        update(i, { status: "refused", note: problems.join("; ") });
        continue;
      }
      update(i, { status: "submitting" });
      try {
        const payload = force ? { ...body, force_reprocess: true } : body;
        const res = await submitRawDocument(payload, tenantId);
        const deduped = (res.status || "").toLowerCase() === "completed";
        update(i, {
          status: deduped ? "deduped" : "queued",
          jobId: res.job_id,
          documentId: res.document_id,
        });
      } catch (e: unknown) {
        update(i, {
          status: "refused",
          note: e instanceof Error ? e.message : String(e),
        });
      }
      await new Promise((r) => setTimeout(r, BATCH_GAP_MS));
    }

    // Then follow whatever is actually running.
    const pending = () =>
      live.map((s, i) => ({ s, i })).filter(({ s }) => s.status === "queued");

    const deadline = Date.now() + 10 * 60 * 1000;
    while (
      pending().length > 0 &&
      Date.now() < deadline &&
      !cancelled.current
    ) {
      await new Promise((r) => setTimeout(r, 3000));
      for (const { s, i } of pending()) {
        if (cancelled.current) break;
        try {
          const d = await apiGet<DocumentDetail>(jobStatusPath(s.jobId!));
          const st = (d.status || "").toLowerCase();
          if (TERMINAL.includes(st)) {
            update(i, {
              status: st === "completed" ? "completed" : "failed",
              fields: (d.fields || []).length,
            });
          }
        } catch {
          /* a failed poll is not a failed job; try again next tick */
        }
      }
    }

    if (!cancelled.current) {
      for (const { i } of pending()) {
        update(i, {
          note: "still running — open the document to keep watching",
        });
      }
    }
    setRunning(false);
  };

  const done = states?.filter((s) =>
    ["completed", "deduped"].includes(s.status),
  ).length;

  return (
    <>
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">
            Batch — paste or upload a JSON array
          </span>
          {rows ? (
            <span className="pill">
              {rows.length} document{rows.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div className="card-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Exactly the body you would POST, one object per document. Reviewed
            here first — nothing is sent until you press Run.
          </p>

          <div className="form-grid">
            <label className="field field-wide">
              <span className="field-label">
                JSON <span className="field-hint">array of submit bodies</span>
              </span>
              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  review(e.target.value);
                }}
                rows={10}
                spellCheck={false}
                placeholder='[{"application_id":"…","document_id":"…","storage_key":"…","storage_provider":"s3","document_type":"trade_license","consent_given":true}]'
                style={{
                  width: "100%",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12.5,
                  padding: "10px 12px",
                  border: "1px solid var(--border-strong)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface)",
                  color: "var(--ink)",
                }}
              />
            </label>

            <label className="field">
              <span className="field-label">or upload a .json file</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => readFile(e.target.files?.[0] ?? null)}
              />
            </label>

            <label className="field field-check field-wide">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
              />
              <span className="field-label">
                force_reprocess on every row{" "}
                <span className="field-hint">
                  re-run files already ingested, instead of returning the
                  earlier extraction
                </span>
              </span>
            </label>
          </div>

          {parseError ? (
            <div style={{ marginTop: 14 }}>
              <ErrorState message={parseError} />
            </div>
          ) : null}
        </div>
      </div>

      {rows ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">Review</span>
            <span className="muted" style={{ fontSize: 12 }}>
              tenant {tenantId || "(none)"}
            </span>
          </div>
          <div className="card-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>storage_key</th>
                    <th>type</th>
                    <th>application_id</th>
                    <th>provider</th>
                    <th>status</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    states ??
                    rows.map((row) => ({ row, status: "waiting" as const }))
                  ).map((s, i) => {
                    const b = s.row.body;
                    const bad = s.row.problems.length > 0;
                    return (
                      <tr key={i} style={bad ? { opacity: 0.6 } : undefined}>
                        <td className="num">{i + 1}</td>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {String(b.storage_key ?? b.key_id ?? "—")}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {String(b.document_type ?? "unclassified")}
                        </td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {String(b.application_id ?? "—").slice(0, 8)}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {String(b.storage_provider ?? "azure")}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {bad ? (
                            <span
                              className="pill pill-critical"
                              title={s.row.problems.join("; ")}
                            >
                              {s.row.problems[0]}
                            </span>
                          ) : "status" in s && s.status !== "waiting" ? (
                            <BatchStatus s={s as BatchState} />
                          ) : (
                            <span className="muted">ready</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="form-actions">
              <button
                className="btn-primary"
                onClick={run}
                disabled={running || rows.every((r) => r.problems.length > 0)}
              >
                {running
                  ? "Running…"
                  : `Run ${rows.filter((r) => r.problems.length === 0).length} document(s)`}
              </button>
              {states ? (
                <span className="muted" style={{ fontSize: 13 }}>
                  {done} of {states.length} finished
                </span>
              ) : null}
            </div>

            {running ? (
              <p className="muted" style={{ marginBottom: 0, fontSize: 13 }}>
                Each document is accepted by the server and extracted there.
                Closing this tab stops this progress table, not the work —
                results appear under Documents either way.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

function BatchStatus({ s }: { s: BatchState }) {
  const cls =
    s.status === "completed"
      ? "pill-good"
      : s.status === "failed" || s.status === "refused"
        ? "pill-critical"
        : s.status === "deduped"
          ? "pill-warning"
          : "";
  const label =
    s.status === "deduped"
      ? "already extracted"
      : s.status === "completed"
        ? `completed · ${s.fields ?? 0} fields`
        : s.status;

  return (
    <span title={s.note}>
      <span className={`pill ${cls}`}>{label}</span>
      {s.documentId && (s.status === "completed" || s.status === "deduped") ? (
        <>
          {" "}
          <Link to={`/documents/${s.documentId}`} style={{ fontSize: 12 }}>
            open
          </Link>
        </>
      ) : null}
    </span>
  );
}

export function SubmitConsole() {
  const [mode, setMode] = useState<"key" | "file" | "batch">("key");
  // tenant_api_test by default so exercising the API does not quietly land in
  // the same dashboards and spend figures as real ingestion. Editable: pointing
  // it at a real tenant is a deliberate act.
  const [tenantId, setTenantId] = useState("tenant_api_test");
  const [documentType, setDocumentType] = useState("trade_license");
  const [applicationId, setApplicationId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [storageProvider, setStorageProvider] = useState("azure");
  const [storageKey, setStorageKey] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [consentGiven, setConsentGiven] = useState(true);
  const [forceReprocess, setForceReprocess] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [job, setJob] = useState<DocumentDetail | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollNote, setPollNote] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /**
   * The key currently being previewed, not a boolean.
   *
   * Holding the key means the viewer cannot drift from the field: edit the key
   * and the preview closes, rather than showing one document next to another
   * one's key.
   */
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  const timer = useRef<number | null>(null);
  const aborter = useRef<AbortController | null>(null);

  const input: SubmitInput = {
    tenantId,
    documentType,
    applicationId: applicationId.trim() || undefined,
    businessName: businessName.trim() || undefined,
    documentId: documentId.trim() || undefined,
    consentGiven,
    forceReprocess,
    storageProvider,
    storageKey: storageKey.trim() || undefined,
    file: mode === "file" ? file : null,
  };

  /** One place to stop, so no path can leave an interval or a request running. */
  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    aborter.current?.abort();
    aborter.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const startPolling = useCallback(
    (id: string) => {
      const startedAt = Date.now();
      setPolling(true);
      setPollNote(null);

      const tick = async () => {
        const elapsed = Date.now() - startedAt;
        if (elapsed > POLL_CEILING_MS) {
          setPollNote(
            "Still running after five minutes — polling stopped. The document page keeps updating.",
          );
          stopPolling();
          return;
        }

        aborter.current?.abort();
        const ac = new AbortController();
        aborter.current = ac;

        try {
          const next = await apiGet<DocumentDetail>(
            jobStatusPath(id),
            ac.signal,
          );
          if (ac.signal.aborted) return;
          setJob(next);
          if (TERMINAL.includes((next.status || "").toLowerCase())) {
            stopPolling();
            return;
          }
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          // A failed poll is not a failed job: the run continues server-side,
          // so this reports and keeps trying rather than declaring an outcome
          // it does not have.
          setPollNote(e instanceof Error ? e.message : String(e));
        }

        timer.current = window.setTimeout(
          tick,
          pollDelay(Date.now() - startedAt),
        );
      };

      void tick();
    },
    [stopPolling],
  );

  const submit = async () => {
    stopPolling();
    setError(null);
    setResult(null);
    setJob(null);
    setPollNote(null);
    setBusy(true);

    try {
      const res = await submitDocument(input);
      setResult(res);

      // The branch that matters. A dedupe hit is already finished; polling it
      // would wait on a job that completed before this request was made.
      if ((res.status || "").toLowerCase() === "completed") {
        const detail = await apiGet<DocumentDetail>(jobStatusPath(res.job_id));
        setJob(detail);
      } else {
        startPolling(res.job_id);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const deduped = (result?.status || "").toLowerCase() === "completed";
  const canSubmit =
    !busy &&
    (mode === "file" ? !!file : storageKey.trim().length > 0) &&
    tenantId.trim().length > 0;

  return (
    <>
      <Breadcrumb trail={[{ label: "Submit" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Submit a document</h1>
          <p className="page-sub">
            Exercises{" "}
            <span className="mono">
              POST /api/orchestrator/documents/submit
            </span>
            , then follows the job to its result.
          </p>
        </div>
      </div>

      <div className="notice" style={{ marginBottom: 20 }}>
        This is not a sandbox. Every submission runs the real pipeline: it
        creates a document and a job, calls OCR, and beyond triage calls a paid
        model.
      </div>

      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Request</span>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div
              className="field field-wide field-choice"
              role="radiogroup"
              aria-label="How to supply the document"
            >
              <label className="field field-check">
                <input
                  type="radio"
                  name="submit-mode"
                  checked={mode === "key"}
                  onChange={() => setMode("key")}
                />
                <span className="field-label">
                  Storage key{" "}
                  <span className="field-hint">
                    the file is already in blob storage
                  </span>
                </span>
              </label>
              <label className="field field-check">
                <input
                  type="radio"
                  name="submit-mode"
                  checked={mode === "batch"}
                  onChange={() => setMode("batch")}
                />
                <span className="field-label">
                  Batch{" "}
                  <span className="field-hint">
                    paste or upload a JSON array, review, then run
                  </span>
                </span>
              </label>
              <label className="field field-check">
                <input
                  type="radio"
                  name="submit-mode"
                  checked={mode === "file"}
                  onChange={() => setMode("file")}
                />
                <span className="field-label">
                  Upload a file{" "}
                  <span className="field-hint">multipart, 50 MB limit</span>
                </span>
              </label>
            </div>

            <Row label="X-Tenant-ID">
              <input
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              />
            </Row>

            {/* Everything below is per-document, and in batch mode each row
                carries its own. Only the tenant above is shared, because it is
                a header on every request rather than a field in the body. */}
            {mode !== "batch" ? (
              <>
                <Row label="document_type">
                  <input
                    type="text"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                  />
                </Row>

                {mode === "key" ? (
                  <>
                    <Row label="storage_provider">
                      <select
                        value={storageProvider}
                        onChange={(e) => setStorageProvider(e.target.value)}
                      >
                        <option value="azure">azure</option>
                        <option value="s3">s3</option>
                      </select>
                    </Row>
                    <Row label="storage_key">
                      <input
                        type="text"
                        value={storageKey}
                        onChange={(e) => {
                          setStorageKey(e.target.value);
                          // The preview belongs to the key that was previewed. Left
                          // open across an edit it would show the previous
                          // document beside a different key, which is the one
                          // mistake this feature exists to prevent.
                          setPreviewKey(null);
                        }}
                      />
                    </Row>
                  </>
                ) : (
                  <label className="field field-wide">
                    <span className="field-label">
                      file{" "}
                      <span className="field-hint">
                        {file
                          ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB`
                          : "none chosen"}
                      </span>
                    </span>
                    <input
                      type="file"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                )}

                <Row label="application_id (optional, uuid)">
                  <input
                    type="text"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                    placeholder="a1b2c3d4-e5f6-7890-abcd-1234567890ab"
                  />
                </Row>

                <Row label="business_name (optional)">
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Hakimi Bakery L.L.C"
                    disabled={!applicationId.trim()}
                  />
                  {/* Two different sentences, because the disabled state and
                      the overwrite are two different things a user needs to
                      know, and showing the second while the field is greyed out
                      would explain a behaviour they cannot reach yet. */}
                  {/* A span, not a div: Row renders a <label>, whose content
                      model is phrasing content. */}
                  <span
                    className="muted"
                    style={{ display: "block", fontSize: 12, marginTop: 4 }}
                  >
                    {applicationId.trim()
                      ? "Stored against the application and shown in the applications list. Replaces the stored name if it differs."
                      : "Needs an application_id — the name is stored against the application, not the document."}
                  </span>
                </Row>

                <Row label="document_id (optional, uuid)">
                  <input
                    type="text"
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    placeholder="left blank the server mints one"
                  />
                </Row>

                <label className="field field-check field-wide">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                  />
                  <span className="field-label">
                    consent_given{" "}
                    <span className="field-hint">
                      unticked is refused with 403
                    </span>
                  </span>
                </label>

                <label className="field field-check field-wide">
                  <input
                    type="checkbox"
                    checked={forceReprocess}
                    onChange={(e) => setForceReprocess(e.target.checked)}
                  />
                  <span className="field-label">
                    force_reprocess{" "}
                    <span className="field-hint">
                      re-runs a file the platform has already seen, instead of
                      returning the earlier extraction
                    </span>
                  </span>
                </label>
              </>
            ) : null}
          </div>

          {mode === "batch" ? null : (
            <div className="form-actions">
              {/* Storage-key mode only: in upload mode the operator picked the
                file from their own disk and has already seen it. */}
              {mode === "key" ? (
                <button
                  disabled={!storageKey.trim()}
                  title="Fetch and show the blob without submitting it"
                  onClick={() =>
                    setPreviewKey(previewKey ? null : storageKey.trim())
                  }
                >
                  {previewKey ? "Hide preview" : "Preview document"}
                </button>
              ) : null}
              <button
                className="btn-primary"
                onClick={submit}
                disabled={!canSubmit}
              >
                {busy ? "Submitting…" : "Submit"}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(asCurl(input, window.location.origin))
                    ?.then(
                      () => setCopied(true),
                      () => setCopied(false),
                    );
                }}
              >
                {copied ? "Copied" : "Copy as curl"}
              </button>
              {polling ? (
                <button onClick={stopPolling}>Stop polling</button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {mode === "batch" ? <BatchPanel tenantId={tenantId} /> : null}

      {previewKey ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">Preview</span>
            <span className="muted mono" style={{ fontSize: 12 }}>
              {storageProvider}:{previewKey}
            </span>
          </div>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>
              Read straight from storage. Nothing has been submitted and no
              document row exists yet.
            </p>
            {/* The existing viewer, which already probes the URL and reports a
                404 or a wrong content type rather than rendering a blank frame. */}
            <DocumentViewer
              src={storagePreviewPath(previewKey, storageProvider)}
              filename={previewKey}
              height={560}
              missingTitle="No object under that key"
              missingHint={`${storageProvider} has no object stored as "${previewKey}". Check the key, and that the provider above is the one it was written to.`}
            />
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="card">
          <ErrorState message={error} />
        </div>
      ) : null}

      {result ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">Response</span>
            <span className={`pill ${deduped ? "pill-warning" : "pill-good"}`}>
              {deduped ? "200 · already extracted" : "202 · queued"}
            </span>
          </div>
          <div className="card-body">
            {deduped ? (
              <p className="muted" style={{ marginTop: 0 }}>
                This file has been ingested before, so the platform returned the
                earlier extraction without running the pipeline. Tick{" "}
                <span className="mono">force_reprocess</span> to make it run
                again.
              </p>
            ) : null}
            <pre className="secret-value" style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="card">
          <div className="card-head bordered">
            <span className="card-title">Progress</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {polling
                ? "polling…"
                : job
                  ? `status: ${job.status}`
                  : "not started"}
            </span>
          </div>
          <div className="card-body">
            {pollNote ? (
              <p className="muted" style={{ marginTop: 0 }}>
                {pollNote}
              </p>
            ) : null}

            {job ? (
              <>
                <div className="chips" style={{ marginBottom: 12 }}>
                  <span className="chip">status {job.status}</span>
                  {job.ocr_engine ? (
                    <span className="chip">{job.ocr_engine}</span>
                  ) : null}
                  {job.confidence != null ? (
                    <span className="chip">
                      confidence {String(job.confidence)}
                    </span>
                  ) : null}
                  {job.execution_time_ms != null ? (
                    <span className="chip">{fmtMs(job.execution_time_ms)}</span>
                  ) : null}
                  <span className="chip">{job.fields?.length ?? 0} fields</span>
                </div>

                {job.stage_timings ? (
                  <pre
                    className="secret-value"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {JSON.stringify(job.stage_timings, null, 2)}
                  </pre>
                ) : null}

                {/* The full renderer already exists; this links to it rather
                    than growing a second one that can drift from it. */}
                <div style={{ marginTop: 14 }}>
                  <Link to={`/documents/${result.document_id}`}>
                    <button className="btn-primary">Open full result →</button>
                  </Link>
                </div>
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                Waiting for the first status…
              </p>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
